
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Troubleshooting',
  layout: 'guide',
  description: 'Debug OAuth issues faster with proper logging and error handling',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Troubleshooting' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "where-to-start"
      }}>{`Where to start`}</h2>
      <ul>
        <li parentName="ul"><a parentName="li" {...{
            "href": "#validator-end-to-end-failures"
          }}>{`Validator end-to-end failures`}</a>{` — Pre-launch test failing in the Dashboard Validator`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "#common-oauth-errors"
          }}>{`Common OAuth errors`}</a>{` — Connection failing during OAuth`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "#common-integration-issues"
          }}>{`Common integration issues`}</a>{` — API calls returning errors`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "#jwks-validation-issues"
          }}>{`JWKS validation issues`}</a>{` — Token validation failing`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "#logging"
          }}>{`Logging`}</a>{` — Debug a specific flow or unsure where to look`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/authentication#try-the-interactive-demo"
          }}>{`FDX Explorer`}</a>{` — Reference OAuth flow with interactive demo`}</li>
      </ul>
      <h2 {...{
        "id": "validator-end-to-end-failures"
      }}>{`Validator end-to-end failures`}</h2>
      <p>{`The `}<a parentName="p" {...{
          "href": "/core-exchange/docs/testing"
        }}>{`Validator`}</a>{` runs in the Data Partner Dashboard and exercises your discovery, OAuth, and data endpoints before production launch. End-to-end runs can fail before any OAuth traffic reaches your `}<inlineCode parentName="p">{`authorize`}</inlineCode>{` or `}<inlineCode parentName="p">{`token`}</inlineCode>{` endpoints — usually at the discovery step.`}</p>
      <h3 {...{
        "id": "error-getting-oidc-configuration"
      }}><inlineCode parentName="h3">{`Error getting OIDC configuration`}</inlineCode></h3>
      <p><strong parentName="p">{`Issue:`}</strong>{` The Validator end-to-end test fails immediately with `}<inlineCode parentName="p">{`Error getting OIDC configuration. Please also make sure you have allowlisted Plaid IPs`}</inlineCode>{`.`}</p>
      <p><strong parentName="p">{`Cause:`}</strong>{` This message covers two unrelated failure modes that need different fixes:`}</p>
      <ol>
        <li parentName="ol">{`The discovery URL (`}<inlineCode parentName="li">{`/.well-known/openid-configuration`}</inlineCode>{`) is not reachable from the public internet — most often a 404, a firewall or WAF block, or a TLS/DNS error.`}</li>
        <li parentName="ol">{`The discovery URL returns 200 but the response body is missing required fields (see `}<a parentName="li" {...{
            "href": "/core-exchange/docs/authentication/oauth-server-setup#well-known-configuration"
          }}>{`Well-known configuration`}</a>{`).`}</li>
      </ol>
      <p>{`IP allowlisting configuration issues are rarely the cause at this step. Plaid fetches `}<inlineCode parentName="p">{`/.well-known/openid-configuration`}</inlineCode>{` and `}<inlineCode parentName="p">{`jwks_uri`}</inlineCode>{` from infrastructure outside the IP ranges in the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/security#ip-allowlist"
        }}>{`IP allowlist documentation`}</a>{`, so these endpoints must remain reachable from the public internet.`}</p>
      <p><strong parentName="p">{`How to debug, in order:`}</strong></p>
      <ol>
        <li parentName="ol">
          <p parentName="li">{`From a network outside your firewall and any internal IP allowlist, run:`}</p>
          <pre parentName="li"><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -v 'https://YOUR_DOMAIN/.well-known/openid-configuration'
`}</code></pre>
          <p parentName="li">{`Expect HTTP 200 with `}<inlineCode parentName="p">{`Content-Type: application/json`}</inlineCode>{`. If you get 404, 403, a TLS error, or a connection timeout, the URL is not publicly reachable — fix DNS, firewall/WAF rules, or the route itself, then re-run the Validator. Confirm your firewall does not restrict this path to the Plaid IP ranges.`}</p>
        </li>
        <li parentName="ol">
          <p parentName="li">{`If step 1 returns 200, verify the JSON body includes `}<inlineCode parentName="p">{`authorization_endpoint`}</inlineCode>{`, `}<inlineCode parentName="p">{`token_endpoint`}</inlineCode>{`, `}<inlineCode parentName="p">{`response_types_supported`}</inlineCode>{` (with `}<inlineCode parentName="p">{`code`}</inlineCode>{`), `}<inlineCode parentName="p">{`scopes_supported`}</inlineCode>{` (with `}<inlineCode parentName="p">{`offline_access`}</inlineCode>{`, plus `}<inlineCode parentName="p">{`openid`}</inlineCode>{` for OIDC), and `}<inlineCode parentName="p">{`userinfo_endpoint`}</inlineCode>{` and `}<inlineCode parentName="p">{`jwks_uri`}</inlineCode>{` for OIDC. Missing or misnamed fields will fail the Validator with the same generic message.`}</p>
        </li>
        <li parentName="ol">
          <p parentName="li">{`If steps 1 and 2 both pass, the discovery layer is healthy — the underlying failure is on `}<inlineCode parentName="p">{`token_endpoint`}</inlineCode>{`, `}<inlineCode parentName="p">{`userinfo_endpoint`}</inlineCode>{`, or a data endpoint, not discovery. The `}<a parentName="p" {...{
              "href": "/core-exchange/docs/security#ip-allowlist"
            }}>{`IP allowlist`}</a>{` applies to those endpoints; confirm it is configured correctly there.`}</p>
        </li>
      </ol>
      <h2 {...{
        "id": "quick-fixes"
      }}>{`Quick fixes`}</h2>
      <h3 {...{
        "id": "oauth-errors-you-should-return"
      }}>{`OAuth errors you should return`}</h3>
      <p>{`These are standard OAuth error codes your server should return to Plaid:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Error code`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`When to return`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Fix`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`access_denied`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`User cancelled authorization`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Normal user behavior - log but don't alert`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`invalid_grant`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Authorization code expired or was reused`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Reduce latency, expire codes in 10 min, enforce single-use`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`temporarily_unavailable`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Your server is overloaded or in maintenance`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Check capacity, implement rate limiting, add monitoring`}</td>
          </tr>
        </tbody>
      </table>
      <h3 {...{
        "id": "jwks-and-token-validation-issues"
      }}>{`JWKS and token validation issues`}</h3>
      <p>{`If Plaid encounters problems validating your ID tokens, connections will fail and your conversion rates will drop. These issues typically show up as authentication failures in your logs or low conversion metrics in the Data Partner Dashboard. Your Solutions Engineer may reach out to help troubleshoot.`}</p>
      <h4 {...{
        "id": "common-jwks-problems-and-fixes"
      }}>{`Common JWKS problems and fixes`}</h4>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Problem`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`How you would notice`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`What to check`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`ID token signature validation failing`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Connection failures, user complaints`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Verify `}<inlineCode parentName="td">{`kid`}</inlineCode>{` in token header matches a key in your published JWKS`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Key rotation issues`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Sudden spike in auth failures after key rotation`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Confirm new keys published to JWKS before removing old ones (24-hour overlap)`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`JWKS endpoint unreachable`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`All connections failing, Plaid can't validate any tokens`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Check endpoint uptime, DNS resolution, firewall rules, and HTTPS configuration`}</td>
          </tr>
        </tbody>
      </table>
      <h2 {...{
        "id": "logging"
      }}>{`Logging`}</h2>
      <p>{`Good logging significantly reduces debugging time. Log the following identifiers for every session:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Identifier`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Purpose`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`state`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Correlates redirect and token exchange.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`userId`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Your internal authenticated user ID.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`timestamp`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`ISO-8601 timestamp of each request/response.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`X-Request-ID`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Plaid request identifier sent in request headers. Correlate with the `}<a parentName="td" {...{
                "href": "/core-exchange/docs/post-launch/dashboard"
              }}>{`Dashboard's Integration Health view`}</a>{`.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`oauth_session_id`}</inlineCode>{` `}<em parentName="td">{`(optional)`}</em></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Your unique session tracker.`}</td>
          </tr>
        </tbody>
      </table>
      <p>{`Plaid sends the `}<inlineCode parentName="p">{`X-Request-ID`}</inlineCode>{` header when calling your `}<inlineCode parentName="p">{`token_endpoint`}</inlineCode>{` and resource server endpoints:`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-http",
          "metastring": "# Example request header",
          "#": true,
          "Example": true,
          "request": true,
          "header": true
        }}>{`X-Request-ID: req_4h8j2k9m3n7p5q1r
`}</code></pre>
      <p>{`Include these identifiers in support tickets to speed up resolution.`}</p>
      <h2 {...{
        "id": "jwks-validation-issues"
      }}>{`JWKS validation issues`}</h2>
      <Callout mdxType="Callout">
  These issues only apply to OIDC implementations that issue ID tokens. Plain
  OAuth 2.0 implementations don't use JWKS.
      </Callout>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Error`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Resolution`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`invalid_signature`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`ID token signature cannot be verified.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Ensure JWKS is reachable and current.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`kid_not_found`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Token `}<inlineCode parentName="td">{`kid`}</inlineCode>{` does not match any published key.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Verify correct key rotation and header values.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`jwks_uri_unreachable`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`JWKS endpoint not available.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Confirm HTTPS accessibility and DNS resolution.`}</td>
          </tr>
        </tbody>
      </table>
      <h2 {...{
        "id": "common-oauth-errors"
      }}>{`Common OAuth errors`}</h2>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Error`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Meaning`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Resolution`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`invalid_request`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Missing/invalid parameter.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Validate query parameters; ensure `}<inlineCode parentName="td">{`redirect_uri`}</inlineCode>{` and `}<inlineCode parentName="td">{`scope`}</inlineCode>{` are present.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`invalid_client`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Client authentication failed.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Confirm `}<inlineCode parentName="td">{`client_id`}</inlineCode>{`/`}<inlineCode parentName="td">{`secret`}</inlineCode>{`.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`unauthorized_client`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The client is not authorized to use this grant type.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Confirm the grant types enabled for Plaid's `}<inlineCode parentName="td">{`client_id`}</inlineCode>{`.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`invalid_grant`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Code expired or reused.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Make codes one-time use; expire within < 10 min.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`temporarily_unavailable`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`DP downtime or maintenance.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Retry; notify Plaid of planned maintenance.`}</td>
          </tr>
        </tbody>
      </table>
      <p>{`A `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{` that doesn't match the value registered for Plaid's `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` has no error code, because you should not redirect at all in that case — show an error page instead. See `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-flow#when-things-go-wrong"
        }}>{`What if the `}<inlineCode parentName="a">{`redirect_uri`}</inlineCode>{` doesn't match?`}</a>{`.`}</p>
      <h2 {...{
        "id": "monitoring-and-alerting"
      }}>{`Monitoring and alerting`}</h2>
      <p>{`Plaid sends email notifications when integration issues are detected, with a link to the issue in your dashboard. Use the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/post-launch/dashboard#issue-center"
        }}>{`Issue Center`}</a>{` to see all active issues at a glance, and drill into individual issue detail pages to debug using error trends, sample errors, and request-level logs.`}</p>
      <p>{`In addition to responding to alerts, proactively monitor:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`HTTP status codes and latency`}</strong>{` for `}<inlineCode parentName="li">{`authorize`}</inlineCode>{`, `}<inlineCode parentName="li">{`token`}</inlineCode>{`, `}<inlineCode parentName="li">{`userinfo`}</inlineCode>{`, and `}<inlineCode parentName="li">{`jwks_uri`}</inlineCode>{` endpoints`}</li>
        <li parentName="ul"><strong parentName="li">{`Error rates`}</strong>{`: Alert if > 2%`}</li>
        <li parentName="ul"><strong parentName="li">{`OAuth and JWKS errors`}</strong>{`: Log with timestamp and state`}</li>
      </ul>
      <h2 {...{
        "id": "when-you-need-help"
      }}>{`When you need help`}</h2>
      <p>{`Opening a support ticket? Include:`}</p>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`state`}</inlineCode>{` value`}</li>
        <li parentName="ul">{`Timestamp and user ID`}</li>
        <li parentName="ul">{`Error message and HTTP status`}</li>
        <li parentName="ul">{`Request/response headers (mask the secrets!)`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`X-Request-ID`}</inlineCode>{` from the request headers`}</li>
        <li parentName="ul">{`Recent log entries surrounding the error`}</li>
      </ul>
      <p>{`The more context you provide, the faster the resolution.`}</p>
      <h2 {...{
        "id": "common-integration-issues"
      }}>{`Common integration issues`}</h2>
      <p>{`Beyond standard OAuth errors, watch for these implementation problems:`}</p>
      <h3 {...{
        "id": "validation-failures"
      }}>{`Validation failures`}</h3>
      <p><strong parentName="p">{`Issue:`}</strong>{` API response validation fails because a field contains `}<inlineCode parentName="p">{`null`}</inlineCode>{` instead of the expected data type (string, number, array, etc.).`}</p>
      <p><strong parentName="p">{`Cause:`}</strong>{` Returning `}<inlineCode parentName="p">{`null`}</inlineCode>{` for a field when the schema expects a typed value.`}</p>
      <p><strong parentName="p">{`Resolution:`}</strong></p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Required fields:`}</strong>{` Return a value in the expected type. If data is unavailable, use an empty string (`}<inlineCode parentName="li">{`""`}</inlineCode>{`) for strings or an empty array (`}<inlineCode parentName="li">{`[]`}</inlineCode>{`) for arrays.`}</li>
        <li parentName="ul"><strong parentName="li">{`Optional fields:`}</strong>{` Omit the field entirely from the response rather than setting it to `}<inlineCode parentName="li">{`null`}</inlineCode>{`.`}</li>
      </ul>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Data examples",
          "#": true,
          "Data": true,
          "examples": true
        }}>{`// Don't do this
{
  "email": null,
  "phoneNumbers": null
}

// Do this instead
{
  "email": "",
  "phoneNumbers": []
}

// Or this (omit optional fields)
{
  "email": ""
}
`}</code></pre>
      <h3 {...{
        "id": "iteration-limit-exceeded"
      }}>{`Iteration limit exceeded`}</h3>
      <p><strong parentName="p">{`Issue:`}</strong>{` Plaid limits how many times it will call a paginated endpoint before throwing an error.`}</p>
      <p><strong parentName="p">{`Cause:`}</strong>{` The `}<inlineCode parentName="p">{`nextOffset`}</inlineCode>{` field (or equivalent pagination parameter) is set to `}<inlineCode parentName="p">{`null`}</inlineCode>{` or an empty string on the final page instead of being omitted.`}</p>
      <p><strong parentName="p">{`Resolution:`}</strong>{` On the last page of results, omit the `}<inlineCode parentName="p">{`nextOffset`}</inlineCode>{` field completely. Don't set it to `}<inlineCode parentName="p">{`null`}</inlineCode>{`, empty string, or `}<inlineCode parentName="p">{`0`}</inlineCode>{`.`}</p>
      <p><strong parentName="p">{`Example:`}</strong></p>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Pagination example",
          "#": true,
          "Pagination": true,
          "example": true
        }}>{`// Don't do this (last page)
{
  "data": [...],
  "nextOffset": null
}

// Do this instead (last page)
{
  "data": [...]
}
`}</code></pre>
      <h3 {...{
        "id": "no-logs-visible-in-data-partner-dashboard"
      }}>{`No logs visible in Data Partner Dashboard`}</h3>
      <p><strong parentName="p">{`Issue:`}</strong>{` Data Providers trying to debug OAuth issues don't see logs in the Data Partner Dashboard.`}</p>
      <p><strong parentName="p">{`Cause:`}</strong>{` Plaid only provides logs after the user successfully passes initial authentication steps. If the OAuth flow fails during authorization or before the token exchange completes, logs will not be available.`}</p>
      <p><strong parentName="p">{`Resolution:`}</strong>{` Contact Plaid support to review internal logs and identify where the flow broke. Common pre-log issues include: invalid `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, HTTPS errors, or redirect URI mismatch.`}</p>
      <h3 {...{
        "id": "invalid_grant-during-refresh"
      }}><inlineCode parentName="h3">{`invalid_grant`}</inlineCode>{` during refresh`}</h3>
      <p><strong parentName="p">{`Issue:`}</strong>{` Refresh token flow fails with `}<inlineCode parentName="p">{`invalid_grant`}</inlineCode>{` error during background updates.`}</p>
      <p><strong parentName="p">{`Cause:`}</strong>{` Refresh token expired before Plaid attempted to use it. This error can occur when the refresh token lifetime is set too short or when the DP revokes tokens after a period of inactivity.`}</p>
      <p><strong parentName="p">{`Resolution:`}</strong></p>
      <ul>
        <li parentName="ul">{`Set refresh token expiration to 13+ months`}</li>
        <li parentName="ul">{`Don't use inactivity timeouts on refresh tokens`}</li>
        <li parentName="ul">{`If revocation is necessary, coordinate with Plaid to avoid breaking active connections`}</li>
      </ul>
      <h3 {...{
        "id": "user-identifier-rejected-consistency-key"
      }}>{`User identifier rejected (consistency key)`}</h3>
      <p><strong parentName="p">{`Issue:`}</strong>{` Connection fails because the user identifier (consistency key) returned in the `}<inlineCode parentName="p">{`id_token`}</inlineCode>{` `}<inlineCode parentName="p">{`sub`}</inlineCode>{` field or `}<inlineCode parentName="p">{`/customers/current`}</inlineCode>{` is fewer than seven characters.`}</p>
      <p><strong parentName="p">{`Cause:`}</strong>{` Consistency key too short to meet minimum requirements.`}</p>
      <p><strong parentName="p">{`Resolution:`}</strong>{` Use a longer identifier or a prefix with the institution code, with a minimum length of seven characters.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# User ID examples",
          "#": true,
          "User": true,
          "ID": true,
          "examples": true
        }}>{`// Too short
{"sub": "12345"}

// Acceptable length
{"sub": "user_12345"}
`}</code></pre>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "where-to-start",
        level: 2,
        title: "Where to start",
      },
{
        id: "validator-end-to-end-failures",
        level: 2,
        title: "Validator end-to-end failures",
      },
{
        id: "error-getting-oidc-configuration",
        level: 3,
        title: "Error getting OIDC configuration",
      },
{
        id: "quick-fixes",
        level: 2,
        title: "Quick fixes",
      },
{
        id: "oauth-errors-you-should-return",
        level: 3,
        title: "OAuth errors you should return",
      },
{
        id: "jwks-and-token-validation-issues",
        level: 3,
        title: "JWKS and token validation issues",
      },
{
        id: "common-jwks-problems-and-fixes",
        level: 4,
        title: "Common JWKS problems and fixes",
      },
{
        id: "logging",
        level: 2,
        title: "Logging",
      },
{
        id: "jwks-validation-issues",
        level: 2,
        title: "JWKS validation issues",
      },
{
        id: "common-oauth-errors",
        level: 2,
        title: "Common OAuth errors",
      },
{
        id: "monitoring-and-alerting",
        level: 2,
        title: "Monitoring and alerting",
      },
{
        id: "when-you-need-help",
        level: 2,
        title: "When you need help",
      },
{
        id: "common-integration-issues",
        level: 2,
        title: "Common integration issues",
      },
{
        id: "validation-failures",
        level: 3,
        title: "Validation failures",
      },
{
        id: "iteration-limit-exceeded",
        level: 3,
        title: "Iteration limit exceeded",
      },
{
        id: "no-logs-visible-in-data-partner-dashboard",
        level: 3,
        title: "No logs visible in Data Partner Dashboard",
      },
{
        id: "invalid_grant-during-refresh",
        level: 3,
        title: "invalid_grant during refresh",
      },
{
        id: "user-identifier-rejected-consistency-key",
        level: 3,
        title: "User identifier rejected (consistency key)",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
